import { Section, SettingRow, NumberInput } from "./SettingsControls";
import { useFinApi } from "src/hooks/useFinApi";
import { useApi } from "src/hooks/useApi";
import { apiRequest, ApiError } from "src/services/apiClient";
import { useTranslation } from "react-i18next";
import { MdRefresh, MdLinkOff, MdLink } from "react-icons/md";
import { fmt } from "../Dashboard/DashboardPrimitives";
import { useEffect, useRef, useState } from "react";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";

const BANK_TX_LIMIT_MIN = 50;
const BANK_TX_LIMIT_MAX = 500;
const BANK_TX_LIMIT_DEFAULT = 100;

export function FinApiConnectionSettings() {
    const { t } = useTranslation();
    const { loading, connected, accounts, lastSyncAt, connect, sync, disconnect, connecting, connectError } = useFinApi();
    const [cooldownUntil, setCooldownUntil] = useState<number>(0);
    const pendingPopupRef = useRef<Window | null>(null);

    // How many recent transactions to fetch/show — its own small self-contained control
    // (fetch + save-on-blur) rather than plugging into the big page-level Settings
    // dirty/Save flow, matching this section's existing instant-action style
    // (connect/sync/disconnect all apply immediately too).
    const { data: txLimitSettings } = useApi<{ bankTransactionLimit?: number }>("/settings");
    const [txLimit, setTxLimit] = useState<number>(BANK_TX_LIMIT_DEFAULT);
    const [txLimitSaving, setTxLimitSaving] = useState(false);
    const [txLimitError, setTxLimitError] = useState<string | null>(null);
    const [txLimitSaved, setTxLimitSaved] = useState(false);
    const savedTxLimitRef = useRef(BANK_TX_LIMIT_DEFAULT);

    useEffect(() => {
        if (txLimitSettings?.bankTransactionLimit == null) return;
        setTxLimit(txLimitSettings.bankTransactionLimit);
        savedTxLimitRef.current = txLimitSettings.bankTransactionLimit;
    }, [txLimitSettings?.bankTransactionLimit]);

    const handleTxLimitBlur = async (raw: number) => {
        const clamped = Math.min(BANK_TX_LIMIT_MAX, Math.max(BANK_TX_LIMIT_MIN, Math.round(raw) || BANK_TX_LIMIT_DEFAULT));
        setTxLimit(clamped);
        if (clamped === savedTxLimitRef.current) return;
        setTxLimitSaving(true);
        setTxLimitError(null);
        setTxLimitSaved(false);
        try {
            await apiRequest(
                "/settings/bankTransactions",
                { method: "PATCH", body: JSON.stringify({ bankTransactionLimit: clamped }) },
                { "Content-Type": "application/json" },
            );
            savedTxLimitRef.current = clamped;
            setTxLimitSaved(true);
            window.setTimeout(() => setTxLimitSaved(false), 2000);
        } catch (e) {
            setTxLimitError(e instanceof ApiError ? e.message : t("common.saveFailed"));
            setTxLimit(savedTxLimitRef.current);
        } finally {
            setTxLimitSaving(false);
        }
    };

    // Clear cooldown automatically so the UI re-enables Retry without any other re-render trigger.
    useEffect(() => {
        if (!cooldownUntil) return;
        const ms = Math.max(0, cooldownUntil - Date.now());
        if (ms === 0) {
            setCooldownUntil(0);
            return;
        }
        const id = window.setTimeout(() => setCooldownUntil(0), ms + 25);
        return () => window.clearTimeout(id);
    }, [cooldownUntil]);

    const inCooldown = cooldownUntil > 0;

    const handleConnect = async () => {
        if (connecting || inCooldown) return;

        // Open a window synchronously (user gesture) so browsers/extensions don't block
        // navigation that happens after an async await.
        // If blocked, we'll fall back to same-tab navigation.
        if (!pendingPopupRef.current || pendingPopupRef.current.closed) {
            try {
                // NOTE: don't use noopener/noreferrer here — we need a handle to set location after await.
                pendingPopupRef.current = window.open("about:blank", "_blank");
                // Provide immediate feedback in the new tab (same-origin about:blank).
                pendingPopupRef.current?.document.write(
                    "<!doctype html><html><head><meta charset='utf-8'/>" +
                    "<meta name='viewport' content='width=device-width, initial-scale=1'/>" +
                    "<title>Connecting…</title>" +
                    "<style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;margin:0;padding:28px;background:#0b3f9c;color:#fff}h1{font-size:18px;margin:0 0 10px}p{opacity:.85;line-height:1.5}</style>" +
                    "</head><body><h1>Connecting to your bank…</h1><p>Please wait. This tab will redirect automatically.</p></body></html>"
                );
                pendingPopupRef.current?.document.close();
            } catch {
                pendingPopupRef.current = null;
            }
        }

        const formUrl = await connect();
        if (formUrl) {
            const popup = pendingPopupRef.current;
            pendingPopupRef.current = null;
            try {
                if (popup && !popup.closed) {
                    popup.location.href = formUrl;
                    popup.focus?.();
                } else {
                    window.location.href = formUrl;
                }
            } catch {
                try { popup?.close(); } catch {}
                window.location.href = formUrl;
            }
        } else {
            // Keep the popup open and show a helpful error instead of closing immediately.
            const popup = pendingPopupRef.current;
            pendingPopupRef.current = null;
            try {
                if (popup && !popup.closed) {
                    popup.document.open();
                    popup.document.write(
                        "<!doctype html><html><head><meta charset='utf-8'/>" +
                        "<meta name='viewport' content='width=device-width, initial-scale=1'/>" +
                        "<title>Connection failed</title>" +
                        "<style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;margin:0;padding:28px;background:#0b3f9c;color:#fff}h1{font-size:18px;margin:0 0 10px}p{opacity:.9;line-height:1.5}code{background:rgba(255,255,255,.15);padding:2px 6px;border-radius:6px}</style>" +
                        "</head><body><h1>Could not start bank connection</h1><p>Please go back to the app and click <strong>Retry</strong>.</p><p>If this persists, the provider may be temporarily locked.</p></body></html>"
                    );
                    popup.document.close();
                }
            } catch {}
            setCooldownUntil(Date.now() + 1500);
        }
    };

    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
    const handleDisconnect = async () => {
        setShowDisconnectConfirm(false);
        await disconnect();
    };

    if (loading) {
        return (
            <Section title={t("finapi.bankConnection")} description={t("settings.bankConnectionDesc")} sticky={false} bodyClassName="body-fin">
                <div className="finapi-row">
                    <span className="skeleton finapi-settings-skeleton" />
                </div>
            </Section>
        );
    }

    return (
        <Section title={t("finapi.bankConnection")} description={t("settings.bankConnectionDesc")} sticky={false} bodyClassName="body-fin">
            {/* Status row */}
            <div className="finapi-row">
                <span className={`finapi-settings-dot finapi-settings-dot--${connected ? "on" : "off"}`} />
                <span className="finapi-row__status">
                    {connected ? t("finapi.connected") : t("finapi.notConnected")}
                </span>
                {connected && lastSyncAt && (
                    <span className="finapi-row__sync-time">
                        {t("finapi.lastSync")}: {new Date(lastSyncAt).toLocaleString("de-DE")}
                    </span>
                )}
                <div className="finapi-row__actions">
                    {connected ? (
                        <>
                            <button className="finapi-action-btn" onClick={sync} title={t("finapi.sync")}>
                                <MdRefresh />
                            </button>
                            <button className="finapi-action-btn finapi-action-btn--danger" onClick={() => setShowDisconnectConfirm(true)} title={t("finapi.disconnect")}>
                                <MdLinkOff />
                            </button>
                            {showDisconnectConfirm && (
                                <ConfirmPopup
                                    message={t("finapi.confirmDisconnect")}
                                    onConfirm={handleDisconnect}
                                    onCancel={() => setShowDisconnectConfirm(false)}
                                />
                            )}
                        </>
                    ) : (
                        <button
                            className="finapi-action-btn finapi-action-btn--primary"
                            onClick={handleConnect}
                            disabled={connecting || inCooldown}
                        >
                            <MdLink /> {connecting ? t("finapi.connecting") : t("finapi.connectBtn")}
                        </button>
                    )}
                </div>
            </div>

            {/* Description when not connected */}
            {!connected && (
                <>
                    <p className="finapi-settings-desc">{t("finapi.settingsDesc")}</p>
                    {/* The bank writes the TAN text itself — usually a generic "data request" without
                        Medinvoice's name — so say so up front instead of leaving doctors to wonder. */}
                    <p className="finapi-settings-desc">{t("finapi.tanNotice")}</p>
                    {connectError?.code === "LOCKED" && (
                        <p className="finapi-settings-desc finapi-settings-desc--warning">
                            {t("finapi.lockedNotice")}
                        </p>
                    )}
                    {connectError?.code === "GENERIC" && (
                        <p className="finapi-settings-desc finapi-settings-desc--error">
                            {t("finapi.connectFailed")}
                            {connectError?.message ? ` (${connectError.message})` : ""}
                        </p>
                    )}
                    {(connectError != null) && (
                        <div style={{ marginTop: 10 }}>
                            <button
                                className="finapi-action-btn finapi-action-btn--primary"
                                onClick={handleConnect}
                                disabled={connecting || inCooldown}
                            >
                                <MdLink /> {t("finapi.retry")}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Account list when connected */}
            {connected && accounts.length > 0 && (
                <div className="finapi-settings-accounts">
                    {accounts.map(acc => (
                        <div key={acc.id} className="finapi-settings-account">
                            <div className="finapi-settings-account__info">
                                <span className="finapi-settings-account__name">{acc.accountName}</span>
                                {acc.iban && <span className="finapi-settings-account__iban">{acc.iban}</span>}
                            </div>
                            <span className={`finapi-settings-account__balance${acc.balance < 0 ? " finapi-settings-account__balance--neg" : ""}`}>
                                {fmt(acc.balance)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Not gated behind `connected` — it's a fetch-size preference, not tied to an
                active session, and should be settable/visible either way. */}
            <SettingRow
                title={t("finapi.transactionLimitLabel")}
                description={t("finapi.transactionLimitDesc", { min: BANK_TX_LIMIT_MIN, max: BANK_TX_LIMIT_MAX })}
            >
                <div className="input-group">
                    <NumberInput
                        value={txLimit}
                        onChange={setTxLimit}
                        onBlur={handleTxLimitBlur}
                        min={BANK_TX_LIMIT_MIN}
                        max={BANK_TX_LIMIT_MAX}
                        label={t("finapi.transactionLimitLabel")}
                    />
                    {txLimitSaving && <span className="finapi-row__sync-time">{t("common.saving")}</span>}
                    {txLimitSaved && !txLimitSaving && <span className="finapi-row__sync-time">{t("common.saved")}</span>}
                    {txLimitError && <span className="finapi-settings-desc finapi-settings-desc--error">{txLimitError}</span>}
                </div>
            </SettingRow>
        </Section>
    );
}
